import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, CircularProgress, Tabs, Tab, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Refresh, TrendingUp, TrendingDown, OpenInNew, ReceiptLong,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import { costProfitService, CostCenter360 } from '../../services/costProfitService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { StatusDot, StatusTone, TableSurface, headerCellSx, bodyRowSx, ErrorState } from '../../components/common/pageKit';

const utilTone = (u: number | null): StatusTone => (u == null ? 'neutral' : u > 100 ? 'error' : u >= 90 ? 'warning' : 'success');

const Kpi: React.FC<{ label: string; value: string; color?: string; sub?: React.ReactNode }> = ({ label, value, color, sub }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: color || 'text.primary', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub}
  </Box>
);

const CostCenter360Page: React.FC = () => {
  const { id } = useParams();
  const ccId = Number(id);
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<CostCenter360 | null>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    costProfitService.costCenter360(ccId)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load cost centre'))
      .finally(() => setLoading(false));
  }, [ccId]);
  useEffect(() => { load(); }, [load]);

  const drillAccount = (accountId: number) => navigate(`/finance/ledger?account=${accountId}`);
  const drillVoucher = (journalEntryId: number) => navigate(`/finance/journal/${journalEntryId}`);

  if (loading && !data) return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><ErrorState message={error} onRetry={load} /></Box>;
  if (!data) return null;
  const h = data.header;
  const overBudget = h.forecast > h.budget && h.budget > 0;

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%' }}>
      {/* Sticky header offset for the app shell */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 64 }, zIndex: 3, bgcolor: c.bg, borderBottom: `1px solid ${c.line}`, px: 3, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate('/finance/cost-centers')}><ArrowBack /></IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: c.accent }}>{h.code}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{h.name}</Typography>
                <Chip size="small" label={h.type} sx={{ height: 20, fontWeight: 700, bgcolor: `${c.accent}14`, color: c.accent }} />
                <StatusDot label={h.isActive ? 'Active' : 'Inactive'} tone={h.isActive ? 'success' : 'neutral'} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {data.financialYear}{h.parentName ? ` · Parent ${h.parentName}` : ''}{h.departmentName ? ` · ${h.departmentName}` : ''}{h.managerName ? ` · Manager ${h.managerName}` : ''}{h.plant ? ` · ${h.plant}` : ''}
              </Typography>
            </Box>
          </Stack>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        </Stack>
        {/* KPI row */}
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mt: 1.5, rowGap: 1 }}>
          <Kpi label="Budget" value={formatIndianNumber(h.budget)} />
          <Kpi label="Actual" value={formatIndianNumber(h.actual)} color={c.accent} />
          <Kpi label="Variance" value={formatIndianNumber(h.variance)} color={h.variance >= 0 ? c.good : c.bad} />
          <Kpi label="Utilisation" value={h.utilisation != null ? `${h.utilisation}%` : '—'} color={(h.utilisation || 0) > 100 ? c.bad : c.text} />
          <Kpi label="Forecast @ Completion" value={formatIndianNumber(h.forecast)} color={overBudget ? c.warn : c.good}
            sub={<Stack direction="row" spacing={0.25} alignItems="center">{overBudget ? <TrendingUp sx={{ fontSize: 13, color: c.warn }} /> : <TrendingDown sx={{ fontSize: 13, color: c.good }} />}<Typography variant="caption" sx={{ color: overBudget ? c.warn : c.good, fontWeight: 700 }}>{overBudget ? 'Trending over' : 'On track'}</Typography></Stack>} />
          <Kpi label="FY Elapsed" value={`${Math.round(h.elapsedFraction * 100)}%`} />
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}>
          {['Overview', 'Budget vs Actual', 'Monthly Trend', 'Transactions'].map((t) => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Overview */}
        {tab === 0 && (
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Monthly Actual Cost (Apr-based FY)</Typography>
                <MonthlyChart data={data.monthly} ct={ct} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Top Expense Accounts</Typography>
                <Stack spacing={1}>
                  {data.expenseByAccount.slice(0, 7).map((a) => (
                    <Stack key={a.accountId} direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1, borderRadius: 2, border: `1px solid ${c.line}`, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }} onClick={() => drillAccount(a.accountId)}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.accountCode} · {a.accountName}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.utilisation != null ? `${a.utilisation}% of budget` : 'no budget'}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c.accent }}>{formatIndianNumber(a.actual)}</Typography>
                    </Stack>
                  ))}
                  {!data.expenseByAccount.length && <Typography variant="body2" color="text.secondary">No posted costs yet.</Typography>}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Budget vs Actual */}
        {tab === 1 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Account', 'Budget', 'Actual', 'Variance', 'Utilisation', ''].map((hh, i) => (
                      <TableCell key={hh || i} sx={headerCellSx} align={i >= 1 && i <= 4 ? 'right' : 'left'}>{hh}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.expenseByAccount.map((a) => {
                    const uc = c[utilTone(a.utilisation) === 'error' ? 'bad' : utilTone(a.utilisation) === 'warning' ? 'warn' : 'good'];
                    return (
                      <TableRow key={a.accountId} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }} onClick={() => drillAccount(a.accountId)}>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{a.accountCode} · {a.accountName}</Typography></TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(a.budget)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(a.actual)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: a.variance < 0 ? c.bad : 'inherit' }}>{formatCurrency(a.variance)}</TableCell>
                        <TableCell align="right"><Typography variant="caption" sx={{ fontWeight: 700, color: uc }}>{a.utilisation != null ? `${a.utilisation}%` : '—'}</Typography></TableCell>
                        <TableCell align="right"><OpenInNew sx={{ fontSize: 15, color: c.sub }} /></TableCell>
                      </TableRow>
                    );
                  })}
                  {!data.expenseByAccount.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: c.sub }}>No posted costs in this cost centre.</TableCell></TableRow>}
                  <TableRow sx={{ '& td': { fontWeight: 800, borderTop: `2px solid ${c.line}` } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{formatCurrency(h.budget)}</TableCell>
                    <TableCell align="right">{formatCurrency(h.actual)}</TableCell>
                    <TableCell align="right" sx={{ color: h.variance < 0 ? c.bad : 'inherit' }}>{formatCurrency(h.variance)}</TableCell>
                    <TableCell align="right">{h.utilisation != null ? `${h.utilisation}%` : '—'}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}

        {/* Monthly Trend */}
        {tab === 2 && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Monthly Actual Cost</Typography>
            <MonthlyChart data={data.monthly} ct={ct} height={380} />
          </Paper>
        )}

        {/* Transactions */}
        {tab === 3 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Voucher', 'Account', 'Description', 'Amount', ''].map((hh, i) => (
                      <TableCell key={hh || i} sx={headerCellSx} align={i === 4 ? 'right' : 'left'}>{hh}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((l) => (
                    <TableRow key={l.id} hover sx={bodyRowSx as any}>
                      <TableCell><Typography variant="caption">{new Date(l.entryDate).toLocaleDateString('en-IN')}</Typography></TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', color: c.accent }} onClick={() => drillVoucher(l.journalEntryId)}>
                          <ReceiptLong sx={{ fontSize: 14 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.entryNumber}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => drillAccount(l.accountId)}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.accountCode} · {l.accountName}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.narration || l.description}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: (l.amount || 0) < 0 ? c.good : 'inherit' }}>{formatCurrency(l.amount ?? (l.debit - l.credit))}</TableCell>
                      <TableCell align="right"><OpenInNew sx={{ fontSize: 15, color: c.sub, cursor: 'pointer' }} onClick={() => drillVoucher(l.journalEntryId)} /></TableCell>
                    </TableRow>
                  ))}
                  {!data.lines.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: c.sub }}>No contributing journal lines.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}
      </Box>
    </Box>
  );
};

const MonthlyChart: React.FC<{ data: { month: string; actual: number }[]; ct: any; height?: number }> = ({ data, ct, height = 260 }) => (
  <Box sx={{ height, width: '100%' }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
        <CartesianGrid {...gridProps(ct)} />
        <XAxis dataKey="month" {...axisProps(ct)} />
        <YAxis {...axisProps(ct)} width={54} tickFormatter={(v) => formatIndianNumber(v)} />
        <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any) => [formatCurrency(Number(v)), n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="actual" name="Actual cost" fill={ct.series(1)} radius={[3, 3, 0, 0]} maxBarSize={30} />
        <Line type="monotone" dataKey="actual" name="Trend" stroke={ct.series(0)} strokeWidth={2} dot={false} legendType="none" />
      </ComposedChart>
    </ResponsiveContainer>
  </Box>
);

export default CostCenter360Page;
